import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  Pressable,
  Linking,
  Animated,
  Platform,
  UIManager,
  ActivityIndicator,
  ScrollView,
  RefreshControl,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import type { RootStackParamList } from '../navigation/stackTypes';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { updateUser } from '../store/actions/user-actions';
import { getUserInfo } from '../services/user-service';
import { isSuccessful } from '../services/api-requests';
import { userActions } from '../store/slices/user-slice';
import { AppDispatch } from '../store';
import { getPayoutOnboardingLink } from '../services/stripe-service';
import { notificationService } from '../services/notification-service';
import { useFocusEffect } from '@react-navigation/native';
import PayoutRulesSelect from '../components/PayoutRulesSelect';
import { PayoutRules_t } from '../models/payout-rules-model';
import { savePayoutRules } from '../services/payout-service';
import { UserSessionAttributes } from '../models/user-session-model';
import { 
  getUserSessionObj, 
  generateAuthToken,
  validateAuthToken as validateAuthTokenService 
} from '../services/user-session-service';
import { setSession } from '../store/actions/user-session';
import { listMyChargers } from '../services/charger-service';
import { getPartnerChargers } from '../services/partner-service';
import { BottomSheetModalRef } from '../components/BottomSheet';
import SignInOptions from './SignInOptions';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  loginUser,
  registerUser,
  validateUserToken,
  oauthLogin,
  getActiveCharges
} from '../store/actions/user-actions';
import { getIpAddressFunc } from '../components/common/utils/ip-address';
import { UserInfo } from '../models/user-info';
import { createUserSession } from '../models/user-session-model';
import { createUser } from '../models/login-register-model';
import { storeUserSession } from '../services/helpers/session-helpers';
import RoleService from '../services/role-service';
import { HeaderWithMenu } from '../components/menu';


type OnboardingStatusNavigationProps = NativeStackScreenProps<
  RootStackParamList,
  'OnboardingStatus'
>;

if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const OnboardingStatus: React.FC<OnboardingStatusNavigationProps> = ({
  navigation,
}) => {
  const user = useSelector((state: RootState) => state.user?.userObj);
  const userSession = useSelector((state: RootState) => state.user?.session);
  const [hostingEnabled, setHostingEnabled] = useState<boolean>(
    user?.hostingEnabled
  );
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [isLoadingOnboard, setIsLoadingOnboard] = useState(false);
  const [animatedValues, setAnimatedValues] = useState<Record<
    string,
    Animated.Value
  > | null>(null);
  const [payoutDate, setPayoutDate] = useState<number>(5);
  const [payoutDay, setPayoutDay] = useState<number>(1);
  const [payoutCadence, setPayoutCadence] = useState<'monthly' | 'weekly'>(
    'monthly'
  );
  const [isPayoutCadenceOpen, setPayoutCadenceOpen] = useState(false);
  const [isPayoutDayOpen, setPayoutDayOpen] = useState(false);
  const [myChargers, setMyChargers] = useState<any[]>([]);
  const [chargersLoading, setChargersLoading] = useState(true);
  const [loginSheetOpened, setLoginSheetOpened] = useState(false);
  const [partnerChargers, setPartnerChargers] = useState<any[]>([]);
  const [hasSplitChargers, setHasSplitChargers] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showRefreshHint, setShowRefreshHint] = useState(false);

  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );

  // Bottom sheet ref for login popup
  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);

  useEffect(() => {
    // Create animated values for ALL possible tasks (both pending and completed)
    const allPossibleTasks = [
      'SETUP_BILLING_ADDRESS',
      'ADD_CHARGER',
      'SETUP_CHARGER_SUBSCRIPTION',
      'SETUP_PAYOUT_ACCOUNT',
      'SETUP_PAYOUT_RULES',
      'ACCEPT_PARTNER_SPLIT_AGREEMENT',
      'PARTNER_ADD_CHARGER',
    ];
    setAnimatedValues(
      Object.fromEntries(
        allPossibleTasks.map((task) => [task, new Animated.Value(0)])
      )
    );
  }, [user?.onboarding_tasks, user?.partner_tasks]);

  useEffect(() => {
    const fetchChargers = async () => {
      setChargersLoading(true);
      try {
        const resp = await listMyChargers();
        if (resp && resp.payload && Array.isArray(resp.payload)) {
          setMyChargers(resp.payload);
        } else {
          setMyChargers([]);
        }
      } catch (e) {
        setMyChargers([]);
      } finally {
        setChargersLoading(false);
      }
    };
    
    // Only fetch chargers if user is authenticated and not a guest
    if (session && !RoleService.isGuest(session)) {
      fetchChargers();
    } else {
      setMyChargers([]);
      setChargersLoading(false);
    }
  }, [session]);

  useEffect(() => {
    const fetchPartnerChargers = async () => {
      try {
        // Only fetch if user is a partner
        if (session && RoleService.hasRole(session, 'partner')) {
          const resp = await getPartnerChargers();
          console.log('OnboardingStatus - Partner chargers response:', resp);
          
          // Check if status is not successful (error response)
          if (!isSuccessful(resp.status)) {
            const errorPayload = resp.payload as any;
            console.log('OnboardingStatus - Error payload:', errorPayload);
            
            if (errorPayload?.requiresAgreement && errorPayload?.agreementType === 'SPLIT_PARTNER_TERMS') {
              // Terms need to be accepted - show the task
              console.log('OnboardingStatus - Terms acceptance required, showing task');
              setHasSplitChargers(true);
              setPartnerChargers([]);
            } else {
              setPartnerChargers([]);
              setHasSplitChargers(false);
            }
          } else if (isSuccessful(resp.status) && resp.payload) {
            // Success response - process chargers
            const chargers = (resp.payload as any)?.chargers || [];
            setPartnerChargers(chargers);
            
            // Check if any charger has 'split' permission
            const hasSplit = chargers.some((c: any) => 
              c.permissions?.includes('split')
            );
            setHasSplitChargers(hasSplit);
            console.log('OnboardingStatus - Has split chargers:', hasSplit);
          } else {
            setPartnerChargers([]);
            setHasSplitChargers(false);
          }
        } else {
          setPartnerChargers([]);
          setHasSplitChargers(false);
        }
      } catch (e: any) {
        console.error('OnboardingStatus - Exception fetching partner chargers:', e);
        // Check if the error indicates that terms need to be accepted
        if (e?.payload?.requiresAgreement && e?.payload?.agreementType === 'SPLIT_PARTNER_TERMS') {
          // Terms need to be accepted - show the task
          setHasSplitChargers(true);
          setPartnerChargers([]);
        } else {
          setPartnerChargers([]);
          setHasSplitChargers(false);
        }
      }
    };
    
    fetchPartnerChargers();
  }, [session]);

  const dispatch: AppDispatch = useDispatch();

  // Pull-to-refresh handler
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setShowRefreshHint(false);
    try {
      // Fetch user info
      const res = await getUserInfo();
      if (isSuccessful(res.status)) {
        dispatch(userActions.setUserObj({ userObj: res.payload!.user }));
      }
      
      // Refresh chargers
      if (session && !RoleService.isGuest(session)) {
        try {
          const resp = await listMyChargers();
          if (resp && resp.payload && Array.isArray(resp.payload)) {
            setMyChargers(resp.payload);
          } else {
            setMyChargers([]);
          }
        } catch (e) {
          setMyChargers([]);
        }
        
        // Refresh partner chargers as well
        if (RoleService.hasRole(session, 'partner')) {
          try {
            const partnerResp = await getPartnerChargers();
            
            // Check if status is not successful (error response)
            if (!isSuccessful(partnerResp.status)) {
              const errorPayload = partnerResp.payload as any;
              if (errorPayload?.requiresAgreement && errorPayload?.agreementType === 'SPLIT_PARTNER_TERMS') {
                // Terms need to be accepted - show the task
                setHasSplitChargers(true);
                setPartnerChargers([]);
              } else {
                setPartnerChargers([]);
                setHasSplitChargers(false);
              }
            } else if (isSuccessful(partnerResp.status) && partnerResp.payload) {
              // Success response - process chargers
              const chargers = (partnerResp.payload as any)?.chargers || [];
              setPartnerChargers(chargers);
              
              // Check if any charger has 'split' permission
              const hasSplit = chargers.some((c: any) => 
                c.permissions?.includes('split')
              );
              setHasSplitChargers(hasSplit);
            } else {
              setPartnerChargers([]);
              setHasSplitChargers(false);
            }
          } catch (e: any) {
            // Check if the error indicates that terms need to be accepted
            if (e?.payload?.requiresAgreement && e?.payload?.agreementType === 'SPLIT_PARTNER_TERMS') {
              // Terms need to be accepted - show the task
              setHasSplitChargers(true);
              setPartnerChargers([]);
            } else {
              setPartnerChargers([]);
              setHasSplitChargers(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing onboarding data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, session]);

  // Handle scroll events to show/hide refresh hint
  const handleScroll = useCallback((event: any) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    // Show hint when pulling down (negative offset)
    if (offsetY < -20 && !refreshing) {
      setShowRefreshHint(true);
    } else {
      setShowRefreshHint(false);
    }
  }, [refreshing]);

  const enableHosting = async (enabled: boolean) => {
    await dispatch(
      updateUser(userSession!, {
        host_enabled: enabled,
      })
    );
    const userResponse = await getUserInfo();

    if (isSuccessful(userResponse.status)) {
      dispatch(
        userActions.setUserObj({ userObj: userResponse?.payload?.user })
      );
      setHostingEnabled(enabled);
    }
  };

  useFocusEffect(
    useCallback(() => {
      // this runs every time the screen gains focus
      (async () => {
        const res = await getUserInfo();
        if (isSuccessful(res.status)) {
          dispatch(userActions.setUserObj({ userObj: res.payload!.user }));
        }
        
        // Also refresh chargers when screen gains focus
        if (session && !RoleService.isGuest(session)) {
          try {
            const resp = await listMyChargers();
            if (resp && resp.payload && Array.isArray(resp.payload)) {
              setMyChargers(resp.payload);
            } else {
              setMyChargers([]);
            }
          } catch (e) {
            setMyChargers([]);
          }
          
          // Refresh partner chargers as well
          if (RoleService.hasRole(session, 'partner')) {
            try {
              const partnerResp = await getPartnerChargers();
              
              // Check if status is not successful (error response)
              if (!isSuccessful(partnerResp.status)) {
                const errorPayload = partnerResp.payload as any;
                if (errorPayload?.requiresAgreement && errorPayload?.agreementType === 'SPLIT_PARTNER_TERMS') {
                  // Terms need to be accepted - show the task
                  setHasSplitChargers(true);
                  setPartnerChargers([]);
                } else {
                  setPartnerChargers([]);
                  setHasSplitChargers(false);
                }
              } else if (isSuccessful(partnerResp.status) && partnerResp.payload) {
                // Success response - process chargers
                const chargers = (partnerResp.payload as any)?.chargers || [];
                setPartnerChargers(chargers);
                
                // Check if any charger has 'split' permission
                const hasSplit = chargers.some((c: any) => 
                  c.permissions?.includes('split')
                );
                setHasSplitChargers(hasSplit);
              } else {
                setPartnerChargers([]);
                setHasSplitChargers(false);
              }
            } catch (e: any) {
              // Check if the error indicates that terms need to be accepted
              if (e?.payload?.requiresAgreement && e?.payload?.agreementType === 'SPLIT_PARTNER_TERMS') {
                // Terms need to be accepted - show the task
                setHasSplitChargers(true);
                setPartnerChargers([]);
              } else {
                setPartnerChargers([]);
                setHasSplitChargers(false);
              }
            }
          }
        }
      })();
    }, [dispatch, session])
  );

  // Additional effect to refresh data when session changes (after login)
  useEffect(() => {
    if (session && !RoleService.isGuest(session)) {
      (async () => {
        // Refresh user info
        const res = await getUserInfo();
        if (isSuccessful(res.status)) {
          dispatch(userActions.setUserObj({ userObj: res.payload!.user }));
          setHostingEnabled(res.payload?.user?.hostingEnabled ?? false);
        }
        
        // Refresh chargers
        try {
          const resp = await listMyChargers();
          if (resp && resp.payload && Array.isArray(resp.payload)) {
            setMyChargers(resp.payload);
          } else {
            setMyChargers([]);
          }
        } catch (e) {
          setMyChargers([]);
        } finally {
          setChargersLoading(false);
        }
        
        // Refresh partner chargers as well
        if (RoleService.hasRole(session, 'partner')) {
          try {
            const partnerResp = await getPartnerChargers();
            
            // Check if status is not successful (error response)
            if (!isSuccessful(partnerResp.status)) {
              const errorPayload = partnerResp.payload as any;
              if (errorPayload?.requiresAgreement && errorPayload?.agreementType === 'SPLIT_PARTNER_TERMS') {
                // Terms need to be accepted - show the task
                setHasSplitChargers(true);
                setPartnerChargers([]);
              } else {
                setPartnerChargers([]);
                setHasSplitChargers(false);
              }
            } else if (isSuccessful(partnerResp.status) && partnerResp.payload) {
              // Success response - process chargers
              const chargers = (partnerResp.payload as any)?.chargers || [];
              setPartnerChargers(chargers);
              
              // Check if any charger has 'split' permission
              const hasSplit = chargers.some((c: any) => 
                c.permissions?.includes('split')
              );
              setHasSplitChargers(hasSplit);
            } else {
              setPartnerChargers([]);
              setHasSplitChargers(false);
            }
          } catch (e: any) {
            // Check if the error indicates that terms need to be accepted
            if (e?.payload?.requiresAgreement && e?.payload?.agreementType === 'SPLIT_PARTNER_TERMS') {
              // Terms need to be accepted - show the task
              setHasSplitChargers(true);
              setPartnerChargers([]);
            } else {
              setPartnerChargers([]);
              setHasSplitChargers(false);
            }
          }
        }
      })();
    }
  }, [session?.access_token, dispatch]); // Trigger when session token changes

  useEffect(() => {
    const getUser = async () => {
      const userResponse = await getUserInfo();
      if (isSuccessful(userResponse.status)) {
        dispatch(
          userActions.setUserObj({ userObj: userResponse.payload?.user })
        );
        setHostingEnabled(userResponse.payload?.user?.hostingEnabled ?? false);
      }
    };

    //log in the user
    const getSessionObj = async () => {
      const sessionObj = await getUserSessionObj();
      if (RoleService.isGuest(sessionObj)) {
        // Show login popup instead of navigating away
        if (bottomSheetModalRef.current) {
          bottomSheetModalRef.current.present();
        }
        return;
      }
      dispatch(setSession(sessionObj));
    };

    // Handle authentication token from mobile redirect (web only)
    const handleAuthToken = async () => {
      if (Platform.OS !== 'web') return;

      try {
        // Check if there's an authToken in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const authToken = urlParams.get('authToken');

        if (authToken) {
          console.log('Auth token detected in URL, validating...');
          
          // Validate the token with the backend
          const validationResponse = await validateAuthTokenService(authToken);

          if (
            isSuccessful(validationResponse.status) &&
            validationResponse.payload?.valid &&
            validationResponse.payload?.session
          ) {
            const authenticatedSession = validationResponse.payload.session;
            
            // Store the authenticated session
            await AsyncStorage.setItem('token', authenticatedSession.access_token);
            await AsyncStorage.setItem('IpAddress', `${authenticatedSession.IpAddress ?? ''}`);
            await AsyncStorage.setItem('email', authenticatedSession.email);
            await AsyncStorage.setItem('username', authenticatedSession.username ?? authenticatedSession.email);
            await AsyncStorage.setItem('userId', authenticatedSession.id);
            await storeUserSession(authenticatedSession);

            // Update Redux state
            dispatch(setSession(authenticatedSession));

            // Show success notification
            notificationService.sendNotification(
              'success',
              'Successfully authenticated! Welcome back.'
            );

            // Clean up the URL by removing the authToken parameter
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('authToken');
            window.history.replaceState({}, '', newUrl.toString());

            // Refresh user info with the new session
            await getUser();
          } else {
            console.error('Invalid or expired auth token');
            notificationService.sendNotification(
              'error',
              'Authentication token is invalid or expired. Please try again.'
            );
            
            // Clean up invalid token from URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('authToken');
            window.history.replaceState({}, '', newUrl.toString());
          }
        }
      } catch (error) {
        console.error('Error handling auth token:', error);
        notificationService.sendNotification(
          'error',
          'Failed to authenticate. Please try logging in again.'
        );
      }
    };

    // Execute initialization
    const initialize = async () => {
      // First, try to handle auth token (if present)
      await handleAuthToken();
      
      // Then proceed with normal session check
      await getSessionObj();
      await getUser();
    };

    initialize();
  }, []);

  // OAuth Return Message Listener - same as WelcomeScreen.web.tsx
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Only run on web

    const oauthReturnMessage = async (event: MessageEvent) => {
      //console.log(`processing event.origin: ${event.origin}`);
      // const expectedOrigin = process.env.REACT_NATIVE_API_SERVER;
      // const normalizedOrigin = expectedOrigin?.replace(/:8003$|:8001$/, ''); // Remove default ports
      // const eventOrigin = event.origin.replace(/:8003$|:8001$/, ''); // Normalize event origin
      if (process.env.REACT_NATIVE_ENV === 'dev') {
        if (event.origin !== `https://stage.meanderev.co:8803`) return;
        // } else if(process.env.REACT_NATIVE_ENV === 'stage'){
        //   if (event.origin !== `${process.env.REACT_NATIVE_API_SERVER}`) return;
      } else {
        if (event.origin !== `${process.env.REACT_NATIVE_API_SERVER}:8803`)
          return;
      }

      const profile: UserInfo = event.data as UserInfo;
      const ipAddress = await getIpAddressFunc();
      const { sub, email, firstname, lastname } = profile;
      const authType = event.data.service;

      console.log(`OAuth Debug: ${JSON.stringify(event.data)}`);
      console.log(`OAuth Debug: ${JSON.stringify(event.data.service)}`);

      try {
        // Use the updated loginUser function which handles OAuth routing
        const loginObj = createUserSession({
          email,
          username: email,
          password: sub.toString(), // OAuth sub as password (will be handled securely by loginUser)
          IpAddress: ipAddress || '1.1.1.1',
          logintype: authType,
          service: authType,
          sub: sub.toString(),
        });

        let loginResp = await loginUser(loginObj);

        // If login fails (user doesn't exist), try registration
        if (loginResp === null) {
          const registerObj = createUser({
            email,
            password: '', // OAuth sub as password (will be handled securely by registerUser)
            username: email,
            first_name: firstname,
            last_name: lastname,
            role: ['user'],
            service: authType,
            sub: sub.toString(),
          });

          const registerResp = await registerUser(registerObj);
          
          if (registerResp && Object.keys(registerResp).length > 0) {
            // After registration, try login again
            loginResp = await loginUser(loginObj);
          } else {
            notificationService.sendNotification(
              'error',
              'Registration issue, please try again'
            );
            return;
          }
        }

        if (loginResp) {
          // Validate the token and get proper session data
          const getSessionData = await validateUserToken();
          const newSessionData = getSessionData.payload;
          if (
            isSuccessful(getSessionData.status) &&
            newSessionData &&
            newSessionData.access_token &&
            newSessionData.IpAddress
          ) {
            await storeUserSession(newSessionData);
            dispatch(setSession(newSessionData));

            // Fetch active charges after successful login
            dispatch(getActiveCharges());

            // Close the login sheet
            if (bottomSheetModalRef.current) {
              bottomSheetModalRef.current.dismiss();
            }

            // Refresh user info after login
            const userResponse = await getUserInfo();
            if (isSuccessful(userResponse.status)) {
              dispatch(
                userActions.setUserObj({ userObj: userResponse.payload?.user })
              );
              setHostingEnabled(userResponse.payload?.user?.hostingEnabled ?? false);
            }

            notificationService.sendNotification(
              'success',
              'Login Successful!'
            );
          } else {
            notificationService.sendNotification(
              'error',
              'Login failed: session data incomplete.'
            );
          }
        }
      } catch (error) {
        console.error('OAuth authentication error:', error);
        
        if (error instanceof Error) {
          if (error.message.includes('different OAuth provider')) {
            notificationService.sendNotification(
              'error',
              'Account exists with different sign-in method. Please use the original sign-in method.'
            );
          } else {
            notificationService.sendNotification(
              'error',
              'Authentication failed. Please try again.'
            );
          }
        } else {
          notificationService.sendNotification(
            'error',
            'Authentication failed. Please try again.'
          );
        }
      }

      dispatch(oauthLogin(profile));

      if (event.data.closeWindow) {
        window.close();
      }
    };

    window.addEventListener('message', oauthReturnMessage);

    return () => {
      window.removeEventListener('message', oauthReturnMessage);
    };
  }, [dispatch, navigation, session]);

  // Function to open login popup
  const OpenBottomSheet = () => {
    if (bottomSheetModalRef.current) {
      setLoginSheetOpened(true);
      bottomSheetModalRef.current.present();
    }
  };



  const cadenceOptions = [
    { displayText: 'Weekly', dbText: 'weekly' },
    { displayText: 'Monthly', dbText: 'monthly' },
  ];

  const dayOfWeekOptions = [
    { displayText: 'Sunday', dbValue: 0 },
    { displayText: 'Monday', dbValue: 1 },
    { displayText: 'Tuesday', dbValue: 2 },
    { displayText: 'Wednesday', dbValue: 3 },
    { displayText: 'Thursday', dbValue: 4 },
    { displayText: 'Friday', dbValue: 5 },
    { displayText: 'Saturday', dbValue: 6 },
  ];

  // Define host-only and partner-only tasks
  const hostOnlyTasks = [
    'ADD_CHARGER',
    'SETUP_CHARGER_SUBSCRIPTION',
    'SETUP_PAYOUT_RULES',
  ];
  
  const partnerOnlyTasks = [
    'ACCEPT_PARTNER_SPLIT_AGREEMENT',
    'PARTNER_ADD_CHARGER',
  ];
  
  const sharedTasks = [
    'SETUP_BILLING_ADDRESS',
    'SETUP_PAYOUT_ACCOUNT',
  ];
  
  // Determine which tasks to show based on user role
  // Show ALL tasks relevant to the user's role(s), not just pending ones
  const getVisibleTasks = () => {
    const visibleTasks: string[] = [];
    const isHost = RoleService.hasRole(session, 'host');
    const isPartner = RoleService.hasRole(session, 'partner');
    
    // Add all host tasks if user is a host
    if (isHost) {
      visibleTasks.push(...hostOnlyTasks);
    }
    
    // Add all partner tasks if user is a partner
    if (isPartner) {
      visibleTasks.push(...partnerOnlyTasks);
    }
    
    // Add shared tasks if user has either role
    if (isHost || isPartner) {
      sharedTasks.forEach(task => {
        if (!visibleTasks.includes(task)) {
          visibleTasks.push(task);
        }
      });
    }
    
    return visibleTasks;
  };
  
  const onboardingTasks = getVisibleTasks();
  
  // Helper function to check if a task is completed
  const isTaskCompleted = (task: string) => {
    const inOnboardingTasks = user?.onboarding_tasks?.includes(task);
    const inPartnerTasks = user?.partner_tasks?.includes(task);
    
    // Task is completed if it's NOT in either pending array
    return !inOnboardingTasks && !inPartnerTasks;
  };

  const getTaskIcon = (task: string) => {
    switch (task) {
      case 'SETUP_CHARGER_SUBSCRIPTION':
        return 'credit-card-outline';
      case 'SETUP_PAYOUT_ACCOUNT':
        return 'bank-outline';
      case 'SETUP_PAYOUT_RULES':
        return 'calendar';
      case 'ADD_CHARGER':
          return 'ev-station';
      case 'SETUP_BILLING_ADDRESS':
        return 'home-outline';
      case 'ACCEPT_PARTNER_SPLIT_AGREEMENT':
        return 'file-document-outline';
      case 'PARTNER_ADD_CHARGER':
        return 'ev-station';
      default:
        return 'checkbox-blank-circle-outline';
    }
  };

  const getTaskTitle = (task: string) => {
    switch (task) {
      case 'SETUP_CHARGER_SUBSCRIPTION':
        return Platform.OS === 'web' ? 'Setup Charger Subscription' : 'Become a Host';
      case 'SETUP_PAYOUT_ACCOUNT':
        return 'Setup Payouts (Link to Stripe)';
      case 'SETUP_PAYOUT_RULES':
        return 'Add your payout cadence and date';
      case 'ADD_CHARGER':
        return 'Add Your First Charger';
      case 'SETUP_BILLING_ADDRESS':
        return 'Setup Billing Address';
      case 'ACCEPT_PARTNER_SPLIT_AGREEMENT':
        return 'Accept Split Partner Terms';
      case 'PARTNER_ADD_CHARGER':
        return 'Add Charger to Partner Portal';
      default:
        return task;
    }
  };

  const handleTaskPress = async (task: string) => {
    switch (task) {
      case 'SETUP_CHARGER_SUBSCRIPTION':
        if (Platform.OS === 'web') {
          navigation.navigate('SetupBilling', { returnToOnboarding: true });
        } else {
          // For mobile, generate a secure single-use token and redirect to web
          try {
            const tokenResponse = await generateAuthToken();
            
            if (isSuccessful(tokenResponse.status) && tokenResponse.payload?.token) {
              const webOnboardingUrl = `${process.env.REACT_NATIVE_API_SERVER}/onboardingstatus?authToken=${tokenResponse.payload.token}`;
              
              await Linking.openURL(webOnboardingUrl);
              
              // Optionally show success message
              notificationService.sendNotification(
                'info',
                'Opening billing setup in browser...'
              );
            } else {
              throw new Error('Failed to generate authentication token');
            }
          } catch (err) {
            console.error('Failed to generate auth token or open URL:', err);
            notificationService.sendNotification(
              'error',
              'Failed to open billing setup. Please try again.'
            );
          }
        }
        break;
      case 'SETUP_PAYOUT_ACCOUNT':
        // no navigation needed, this is a link
        break;
      case 'SETUP_PAYOUT_RULES':
        //no navigation needed
        break;
      case 'ADD_CHARGER':
        navigation.navigate('ChargerValidation', { returnToOnboarding: true });
        break;
      case 'SETUP_BILLING_ADDRESS':
        navigation.navigate('SetupBillingAddress');
        break;
      case 'ACCEPT_PARTNER_SPLIT_AGREEMENT':
        // Navigate to partnerships page where they can review and accept terms
        navigation.navigate('SplitPartnerships');
        break;
      case 'PARTNER_ADD_CHARGER':
        // Navigate to Partner Portal where they can add chargers
        navigation.navigate('SplitPartnerships');
        break;
      default:
        break;
    }
  };

  const toggleExpand = (task: string) => {
    const newTask = expandedTask === task ? null : task;
    setExpandedTask((prev) => (prev === task ? null : task));
    
    // Animate for all onboarding tasks (even if already completed)
    if (animatedValues) {
      onboardingTasks.forEach((t) => {
        if (animatedValues[t]) {
          Animated.timing(animatedValues[t], {
            toValue: t === newTask ? 1 : 0,
            duration: 300,
            useNativeDriver: false,
          }).start();
        }
      });
    }
    handleTaskPress(task);
  };

  const handleOnboardPress = useCallback(async () => {
    setIsLoadingOnboard(true);
    try {
      const onboardingLink = await getPayoutOnboardingLink();
      if (
        !isSuccessful(onboardingLink.status) ||
        !onboardingLink.payload?.url
      ) {
        throw new Error('Could not retrieve Stripe onboarding link');
      }
      const url = encodeURI(onboardingLink.payload.url);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        //window.open(url, '_blank', 'noopener,noreferrer');
        //await Linking.openURL(url);
        window.location.href = url;
      } else {
        notificationService.sendNotification(
          'error',
          'Trying to open undefined window'
        );
        const supported = await Linking.canOpenURL(url);
        if (supported) {
          await Linking.openURL(url);
        } else {
          notificationService.sendNotification(
            'error',
            'Cannot open the link.'
          );
        }
      }
    } catch (err: any) {
      notificationService.sendNotification(
        'error',
        err.message || 'Onboarding failed'
      );
    } finally {
      setIsLoadingOnboard(false);
    }
  }, []);

  const handleSave = async (e: React.TouchEvent | any) => {
    const rules: PayoutRules_t = {
      payout_cadence: payoutCadence,
      payout_date: payoutCadence === 'monthly' ? payoutDate : payoutDay,
    };
    const resp = await savePayoutRules(rules);
    console.log(resp);
    if (isSuccessful(resp.status)) {
      notificationService.sendNotification(
        'success',
        'Your settings have been saved!'
      );
      const userResponse = await getUserInfo();
      if (isSuccessful(userResponse.status)) {
        dispatch(
          userActions.setUserObj({ userObj: userResponse?.payload?.user })
        );
      }
      toggleExpand('SETUP_PAYOUT_RULES');
    } else {
      notificationService.sendNotification(
        'error',
        `There was an error setting your rules ${resp.payload?.message}`
      );
    }
  };

  const isPayoutDateInvalid =
    isNaN(payoutDate) || payoutDate < 1 || payoutDate > 31;

  // Hide SETUP_CHARGER_SUBSCRIPTION if user has no chargers
  const isTaskHidden = (task: string) => {
    if (task === 'SETUP_CHARGER_SUBSCRIPTION') {
      // Hide the task if user is not authenticated or has no chargers
      return !session || RoleService.isGuest(session) || !myChargers || myChargers.length === 0;
    }
    if (task === 'ADD_CHARGER') {
      // Hide if billing address is not complete
      return user?.onboarding_tasks?.includes('SETUP_BILLING_ADDRESS');
    }
    if (task === 'ACCEPT_PARTNER_SPLIT_AGREEMENT') {
      // Hide if user doesn't have any chargers with 'split' permission
      return !hasSplitChargers;
    }
    return false;
  };

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea} edges={['top']}>
        {/* Hamburger Menu Header */}
        <HeaderWithMenu forceHamburger title="Onboarding Status" insideSafeArea />
        
        <ScrollView 
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={true}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor="#FFFFFF"
              colors={["#FFFFFF"]}
            />
          }
        >
          {/* Pull to refresh hint - only shown when pulling down */}
          {showRefreshHint && (
            <View style={styles.refreshHintContainer}>
              <Text style={styles.refreshHintText}>Pull down to refresh</Text>
            </View>
          )}
          
          {/* Always show onboarding tasks. Only gate the "Enable Hosting" toggle. */}
          <>
              {onboardingTasks.map((task, index) => {
                if (isTaskHidden(task)) return null;
                if (!animatedValues || !animatedValues[task]) return null;
                const height = animatedValues[task].interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 60],
                });
                const opacity = animatedValues[task];
                return (
                  <View key={index}>
                    <Pressable
                      key={index}
                      style={styles.taskCard}
                      // onPress={() => handleTaskPress(task)}
                      //disabled={!user?.onboarding_tasks.includes(task)}
                      onPress={() => toggleExpand(task)}
                    >
                      <MaterialCommunityIcons
                        name={getTaskIcon(task)}
                        size={24}
                        color="#01556F"
                      />
                      <Text style={styles.taskText}>{getTaskTitle(task)}</Text>
                      {!isTaskCompleted(task) ? (
                        <MaterialCommunityIcons
                          name="chevron-right"
                          size={24}
                          color="#01556F"
                        />
                      ) : (
                        <View style={styles.checkedCircle}>
                          <MaterialCommunityIcons
                            name="check"
                            size={16}
                            color="white"
                          />
                        </View>
                      )}
                    </Pressable>
                    {expandedTask === task &&
                      task === 'SETUP_PAYOUT_ACCOUNT' && (
                        <Animated.View
                          style={{
                            // height,
                            opacity,
                            overflow: 'hidden',
                            paddingHorizontal: 34,
                            paddingBottom: 10,
                          }}
                        >
                          <Text
                            style={{
                              color: '#40BFB4',
                              marginLeft: 34,
                              marginRight: 16,
                              marginBottom: 10,
                            }}
                          >
                            Access your personalized Stripe page and enable
                            payout functionality for your charger earnings.
                          </Text>

                          <Pressable
                            onPress={handleOnboardPress}
                            disabled={isLoadingOnboard}
                            style={({ pressed }) => [
                              {
                                backgroundColor: isLoadingOnboard
                                  ? 'gray'
                                  : '#39BEB7',
                                marginHorizontal: 34,
                                paddingVertical: 12,
                                borderRadius: 45,
                                alignItems: 'center',
                                opacity: pressed ? 0.8 : 1,
                              },
                            ]}
                          >
                            {isLoadingOnboard ? (
                              <ActivityIndicator />
                            ) : (
                              <Text
                                style={{ color: 'white', fontWeight: '600' }}
                              >
                                Tap Here to Onboard
                              </Text>
                            )}
                          </Pressable>
                        </Animated.View>
                      )}
                    {expandedTask === task && task === 'SETUP_PAYOUT_RULES' && (
                      <Animated.View
                        style={{
                          opacity,
                          overflow: 'hidden',
                          paddingHorizontal: 34,
                          paddingBottom: 10,
                        }}
                      >
                        <PayoutRulesSelect
                          isLoadingOnboard={isLoadingOnboard}
                          handleSave={handleSave}
                          setPayoutCadence={setPayoutCadence}
                          setPayoutDate={setPayoutDate}
                          setPayoutDay={setPayoutDay}
                          payoutCadence={payoutCadence}
                          payoutDate={payoutDate}
                          payoutDay={payoutDay}
                          isPayoutCadenceOpen={isPayoutCadenceOpen}
                          setPayoutCadenceOpen={setPayoutCadenceOpen}
                          isPayoutDayOpen={isPayoutDayOpen}
                          setPayoutDayOpen={setPayoutDayOpen}
                        />
                      </Animated.View>
                    )}
                  </View>
                );
              })}
              {RoleService.hasAnyRole(session, ['host', 'admin']) && 
               (user?.onboarding_tasks?.length === 0) && (
                <View style={styles.hostingCard}>
                  <View style={styles.hostingContent}>
                    <MaterialCommunityIcons
                      name="power-socket"
                      size={24}
                      color="#01556F"
                    />
                    <Text style={styles.hostingText}>Enable Hosting</Text>
                  </View>
                  <MaterialCommunityIcons
                    name={
                      hostingEnabled && 
                      user?.onboarding_tasks?.length === 0
                        ? 'toggle-switch'
                        : 'toggle-switch-off'
                    }
                    size={40}
                    color={
                      hostingEnabled && 
                      user?.onboarding_tasks?.length === 0
                        ? '#01556F'
                        : 'darkred'
                    }
                    onPress={() => enableHosting(!hostingEnabled)}
                  />
                </View>
              )}
          </>
        </ScrollView>
        
        {/* Login Popup */}
        <SignInOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetModalRef}
          destination={'OnboardingStatus'}
        />
        
        {/* Monitor login sheet close and navigate if user is not authenticated */}
        {loginSheetOpened && (!session || RoleService.isGuest(session)) && (
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'transparent' }}>
            <Pressable
              style={{ flex: 1 }}
              onPress={() => {
                setLoginSheetOpened(false);
                navigation.navigate('WelcomeScreen');
              }}
            />
          </View>
        )}
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  safeArea: {
    flex: 1,
  },
  header: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
    minHeight: 48,
  },
  titleAbsolute: {
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    zIndex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  refreshHintContainer: {
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 8,
  },
  refreshHintText: {
    color: '#40BFB4',
    fontSize: 12,
    fontStyle: 'italic',
  },
  taskCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  taskText: {
    flex: 1,
    marginLeft: 16,
    fontSize: 16,
    color: '#01556F',
  },
  hostingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  hostingContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  hostingText: {
    marginLeft: 16,
    fontSize: 16,
    color: '#01556F',
  },
  completeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  completeText: {
    marginTop: 16,
    marginBottom: 32,
    fontSize: 18,
    color: 'white',
    textAlign: 'center',
  },
  checkedCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'green',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default OnboardingStatus;
